import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { getDeadlineData, getStatusUI, ESCALATION_LEVELS } from '../../../utils/deadlineRules';

const { FiClock, FiAlertCircle, FiCheckCircle, FiAlertTriangle, FiFilter, FiCalendar, FiArrowRight } = FiIcons;

const InvestigatorSLADashboard = ({ assignments, onSelectCase }) => {
  // Beregn SLA-data for alle oppdrag
  const casesWithSLA = assignments.map(a => ({
    ...a,
    sla: getDeadlineData(a.reports)
  })).filter(a => a.sla !== null);

  const stats = {
    onTrack: casesWithSLA.filter(c => c.sla.level === ESCALATION_LEVELS.NORMAL).length,
    warning: casesWithSLA.filter(c => c.sla.level === ESCALATION_LEVELS.WARNING).length,
    overdue: casesWithSLA.filter(c => c.sla.level >= ESCALATION_LEVELS.OVERDUE).length,
  };

  const getUrgencyScore = (c) => {
    if (c.sla.level === ESCALATION_LEVELS.CRITICAL) return 100;
    if (c.sla.level === ESCALATION_LEVELS.OVERDUE) return 80;
    if (c.sla.level === ESCALATION_LEVELS.WARNING) return 50;
    return 10;
  };

  const sortedCases = [...casesWithSLA].sort((a, b) => getUrgencyScore(b) - getUrgencyScore(a));

  return (
    <div className="space-y-6">
      {/* SLA Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiCheckCircle} className="text-xl" />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">I Rute</p>
            <p className="text-2xl font-black text-gray-900">{stats.onTrack}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiClock} className="text-xl" />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Nærmer seg frist</p>
            <p className="text-2xl font-black text-gray-900">{stats.warning}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiAlertCircle} className="text-xl" />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Frist Utgått</p>
            <p className="text-2xl font-black text-gray-900">{stats.overdue}</p>
          </div>
        </div>
      </div>

      {/* SLA Case List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h3 className="font-black text-gray-900 flex items-center gap-2 uppercase tracking-tight">
            <SafeIcon icon={FiCalendar} className="text-indigo-600" /> Lovpålagte Frister (SLA)
          </h3>
          <div className="flex gap-2">
            <button className="text-[10px] font-black uppercase text-gray-500 bg-white px-3 py-1.5 rounded border border-gray-200 shadow-xs hover:bg-gray-50">
              <SafeIcon icon={FiFilter} className="inline mr-1" /> Filtrer
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {sortedCases.map((c) => {
            const statusUI = getStatusUI(c.sla.level);
            const StatusIcon = statusUI.icon;

            return (
              <motion.div 
                key={c.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-indigo-50/30 transition-all group"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border bg-${statusUI.color}-50 text-${statusUI.color}-600 border-${statusUI.color}-100`}>
                        {statusUI.label}
                      </span>
                      <h4 className="font-bold text-gray-900">{c.reports?.title}</h4>
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-1 mb-2">{c.reports?.description}</p>
                    <div className="flex flex-wrap gap-4 mt-2">
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-gray-400 font-medium">Mottak:</span>
                        <span className={`font-bold ${c.sla.isAcked ? 'text-green-600' : 'text-gray-700'}`}>
                          {c.sla.isAcked ? 'Bekreftet' : `Frist ${new Date(c.sla.deadlineAck).toLocaleDateString()}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs border-l border-gray-200 pl-4">
                        <span className="text-gray-400 font-medium">Tilbakemelding:</span>
                        <span className="font-bold text-gray-700">
                          {new Date(c.sla.deadlineFeedback).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-3 min-w-[200px]">
                    <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 bg-${statusUI.color}-500`}
                        style={{ width: `${getUrgencyScore(c)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between w-full">
                      <p className={`text-[10px] font-black uppercase text-${statusUI.color}-600 flex items-center gap-1`}>
                        <SafeIcon icon={StatusIcon} /> {c.sla.message}
                      </p>
                      <button 
                        onClick={() => onSelectCase(c)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                      >
                        <SafeIcon icon={FiArrowRight} className="text-lg" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {sortedCases.length === 0 && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                <SafeIcon icon={FiCheckCircle} className="text-2xl" />
              </div>
              <h4 className="font-bold text-gray-900">Ingen aktive frister</h4>
              <p className="text-sm text-gray-500">Du har ingen saker som krever umiddelbar handling akkurat nå.</p>
            </div>
          )}
        </div>
      </div>

      {/* Legal Reminder */}
      <div className="bg-indigo-900 text-white p-6 rounded-xl shadow-lg border border-indigo-800 flex items-start gap-4">
        <div className="p-3 bg-indigo-800 rounded-lg">
          <SafeIcon icon={FiAlertTriangle} className="text-indigo-200 text-xl" />
        </div>
        <div>
          <h4 className="font-bold text-indigo-100 mb-1">Viktig påminnelse om Lovpålagte Frister</h4>
          <p className="text-sm text-indigo-200 leading-relaxed">
            I henhold til EU-direktivet for varsling (2019/1937) og Arbeidsmiljøloven, er du som utreder ansvarlig for at varsler mottar bekreftelse innen <strong>7 dager</strong> og en formell tilbakemelding om tiltak innen <strong>3 måneder</strong>. Manglende overholdelse kan medføre sanksjoner for oppdragsgiver.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvestigatorSLADashboard;